import React, { useState, useMemo, useCallback } from 'react'

import { useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { Box, Typography, Stack, IconButton, Tooltip, useTheme } from '@mui/material'

import { sortByDateSelector } from 'data/utils/sortByDateSelector'
import { useGetAllRentalPeriodVersionsQuery, useGetRentalFeeToConfiguredServiceVersionsQuery } from 'api/entity-version/entityVersionCrud'
import { useGetRentalFeesResetHistoryQuery } from 'api/configured-service/configuredServiceRentalFeesCrud'

import T from 'T'
import { HHFormPriceField } from 'components/form-fields/v5'
import { HHAlert } from 'components/common/HHAlert'
import { ReactComponent as FrameIcon } from 'assets/FrameIcon.svg'
import StyledBadge from 'components/notes/common/StyledBadge'
import {
  selectConfiguredServiceId,
  selectRentalFeeResetCount,
  selectIsNewRentalFee,
  selectIsLatestPeriodClosed,
  selectIsLatestPeriodOngoing,
  selectIsOngoingRentalFee,
  selectIsStartDateDeletable,
  selectLatestRentalPeriodStartDate,
  selectSelectedDay,
  selectSelectedDayWorkOrders,
  selectReadOnly,
  selectEditModeDismissed,
} from 'slices/pricing/rentalFeeDialogSlice'
import useRentalFeeActions from './useRentalFeeActions'
import GracePeriodResetHistoryPopover from '../../common/popover/grace-period-reset-history/GracePeriodResetHistoryPopover'
import GracePeriodResetHistoryDialog from '../../common/popover/grace-period-reset-history/GracePeriodResetHistoryDialog'
import {
  getRows,
  normalizeAllPeriodVersionsToRows,
  normalizePriceChangeVersionsToRows,
} from '../../common/popover/grace-period-reset-history/utils'

import RentalFeeCalendarLegend from './RentalFeeCalendarLegend'
import RentalFeeDateChips from './RentalFeeDateChips'
import SelectedDayWorkOrders from './SelectedDayWorkOrders'

const RentalFeeDetailsPanel = () => {
  const theme = useTheme()
  const { watch, control } = useFormContext()
  const rentalFee = watch('rentalFee')

  const configuredServiceId = useSelector(selectConfiguredServiceId)
  const rentalFeeResetCount = useSelector(selectRentalFeeResetCount)
  const isNewRentalFee = useSelector(selectIsNewRentalFee)
  const isLatestPeriodClosed = useSelector(selectIsLatestPeriodClosed)
  const isLatestPeriodOngoing = useSelector(selectIsLatestPeriodOngoing)
  const isOngoingRentalFee = useSelector(selectIsOngoingRentalFee)
  const isStartDateDeletable = useSelector(selectIsStartDateDeletable)
  const latestRentalPeriodStartDateISO = useSelector(selectLatestRentalPeriodStartDate)
  const selectedDayISO = useSelector(selectSelectedDay)
  const selectedDayWorkOrders = useSelector(selectSelectedDayWorkOrders)
  const readOnly = useSelector(selectReadOnly)
  const editModeDismissed = useSelector(selectEditModeDismissed)

  const { handleClearStartDate, handleClearEndDate, handleExitEditMode, handleRevertEditMode, isCreatingNewPeriod, isEndDateDeletable } =
    useRentalFeeActions()

  const latestRentalPeriodStartDate = useMemo(
    () => (latestRentalPeriodStartDateISO ? new Date(latestRentalPeriodStartDateISO) : null),
    [latestRentalPeriodStartDateISO]
  )
  const selectedDay = useMemo(() => (selectedDayISO ? new Date(selectedDayISO) : null), [selectedDayISO])

  const [anchorEl, setAnchorEl] = useState(null)
  const [isOpenActivityDialog, setIsOpenActivityDialog] = useState(false)

  const rentalFeeToConfiguredServiceId = rentalFee?.rentalFeeToConfiguredServiceId

  const { isFetching: isResetLoading, data: resetHistoryData } = useGetRentalFeesResetHistoryQuery(
    { id: configuredServiceId },
    { skip: !configuredServiceId }
  )
  const { isFetching: isPeriodVersionsLoading, data: allPeriodVersionsData } = useGetAllRentalPeriodVersionsQuery(
    { rentalFeeToConfiguredServiceId },
    { skip: !rentalFeeToConfiguredServiceId }
  )
  const { isFetching: isPriceChangeLoading, data: priceChangeVersionsData } = useGetRentalFeeToConfiguredServiceVersionsQuery(
    { rentalFeeToConfiguredServiceId },
    { skip: !rentalFeeToConfiguredServiceId, refetchOnMountOrArgChange: true }
  )

  const periodVersionRows = useMemo(() => normalizeAllPeriodVersionsToRows(allPeriodVersionsData), [allPeriodVersionsData])
  const priceChangeRows = useMemo(() => normalizePriceChangeVersionsToRows(priceChangeVersionsData), [priceChangeVersionsData])

  const graceRows = useMemo(() => getRows(resetHistoryData).map(row => ({ ...row, type: 'grace_reset' })), [resetHistoryData])
  const mergedHistoryRows = useMemo(() => {
    const merged = [...graceRows, ...periodVersionRows, ...priceChangeRows]
    return sortByDateSelector({ data: merged, key: 'createdAt' })
  }, [graceRows, periodVersionRows, priceChangeRows])
  const isHistoryLoading = isResetLoading || isPeriodVersionsLoading || isPriceChangeLoading

  const historyActivityCount = useMemo(
    () => (mergedHistoryRows.length > 0 ? mergedHistoryRows.length : rentalFeeResetCount),
    [mergedHistoryRows.length, rentalFeeResetCount]
  )
  const hasHistory = historyActivityCount > 0

  const displayDates = useMemo(() => {
    if (isOngoingRentalFee && latestRentalPeriodStartDate) {
      return { startDate: latestRentalPeriodStartDate, endDate: rentalFee.endDate }
    }
    return { startDate: rentalFee.startDate, endDate: rentalFee.endDate }
  }, [isOngoingRentalFee, latestRentalPeriodStartDate, rentalFee.startDate, rentalFee.endDate])

  const handlePopoverOpen = useCallback(event => setAnchorEl(event.currentTarget), [])
  const handlePopoverClose = useCallback(() => setAnchorEl(null), [])
  const handleOpenActivityDialog = useCallback(() => setIsOpenActivityDialog(true), [])
  const handleCloseActivityDialog = useCallback(() => setIsOpenActivityDialog(false), [])

  const isEditingExistingPeriod = Boolean(rentalFee?.activePeriodId)
  const shouldShowDateChips = useMemo(() => {
    if (isEditingExistingPeriod) return true
    if (isLatestPeriodClosed && !isCreatingNewPeriod) return false
    if (readOnly) {
      return Boolean(displayDates.startDate || displayDates.endDate) && (isLatestPeriodOngoing || isCreatingNewPeriod)
    }
    return isNewRentalFee || isLatestPeriodOngoing || isCreatingNewPeriod
  }, [
    isEditingExistingPeriod,
    isLatestPeriodClosed,
    isCreatingNewPeriod,
    readOnly,
    displayDates.startDate,
    displayDates.endDate,
    isLatestPeriodOngoing,
    isNewRentalFee,
  ])

  const showStartDateAlert = !readOnly && !shouldShowDateChips
  const effectiveStartDateDeletable = readOnly ? false : isStartDateDeletable
  const effectiveEndDateDeletable = readOnly ? false : isEndDateDeletable

  return (
    <Stack direction="column" spacing={2} mt={2}>
      <Box display="flex" gap={1}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <HHFormPriceField
            fullWidth
            name="rentalFee.amount"
            control={control}
            deprecatedLabel={false}
            label={T.AMOUNT}
            disabled={readOnly}
            size="small"
          />
        </Box>
        <Stack direction="row" alignItems="center">
          <Typography variant="caption" color="textSecondary">
            {T.RENTAL_PERIOD_ACTIVITY}
          </Typography>
          <Tooltip title={T.RENTAL_PERIOD_ACTIVITY} arrow placement="bottom" disableHoverListener={hasHistory} disableInteractive>
            <IconButton
              size="small"
              disabled={!hasHistory}
              onClick={handleOpenActivityDialog}
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
            >
              <StyledBadge badgeContent={historyActivityCount}>
                <FrameIcon />
              </StyledBadge>
            </IconButton>
          </Tooltip>
        </Stack>

        {anchorEl && (
          <GracePeriodResetHistoryPopover
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            rows={mergedHistoryRows}
            isLoading={isHistoryLoading}
          />
        )}
        <GracePeriodResetHistoryDialog
          isOpen={isOpenActivityDialog}
          onClose={handleCloseActivityDialog}
          rows={mergedHistoryRows}
          isLoading={isHistoryLoading}
        />
      </Box>

      <Box sx={{ minWidth: 200 }}>
        <RentalFeeCalendarLegend />
      </Box>

      {showStartDateAlert && (
        <HHAlert severity="info" borderColor={theme.palette.info.light}>
          <Typography variant="body1" color="textSecondary">
            {T.SELECT_DAY_TO_SET_START_DATE}
          </Typography>
        </HHAlert>
      )}

      {shouldShowDateChips && (
        <RentalFeeDateChips
          startDate={displayDates.startDate}
          endDate={displayDates.endDate}
          isStartDateDeletable={effectiveStartDateDeletable}
          isEndDateDeletable={effectiveEndDateDeletable}
          showEditModeActions={isEditingExistingPeriod && !editModeDismissed}
          onClearStartDate={handleClearStartDate}
          onClearEndDate={handleClearEndDate}
          onExitEditMode={readOnly ? undefined : handleExitEditMode}
          onRevertEditMode={readOnly ? undefined : handleRevertEditMode}
        />
      )}

      <SelectedDayWorkOrders selectedDay={selectedDay} workOrders={selectedDayWorkOrders} />
    </Stack>
  )
}

export default RentalFeeDetailsPanel
